import { useState } from "react";
import { FiArrowUpRight } from "react-icons/fi";
import { RxCross2 } from "react-icons/rx";
import { Databases, ID, Client } from "appwrite";

// config.js is a plain script tag, so it can be missing at runtime even though the
// bundle loaded fine. Fail soft here rather than throwing during module import.
const getDatabases = () => {
  const config = window.AppConfig;
  if (!config?.APPWRITE_ENDPOINT || !config?.PROJECT_ID) return null;

  const client = new Client()
    .setEndpoint(config.APPWRITE_ENDPOINT)
    .setProject(config.PROJECT_ID);

  return new Databases(client);
};

const PopUp = ({ showModal, setShowModal }) => {
  const [email, setEmail] = useState("");
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);

  const validateEmail = (em) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(em);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (isSubmitting) return;

    const trimmedEmail = email.trim();

    if (!validateEmail(trimmedEmail)) {
      setSuccess("");
      setError("Please enter a valid email address.");
      return;
    }

    const databases = getDatabases();
    if (!databases) {
      setSuccess("");
      setError("Something went wrong. Try again.");
      return;
    }

    setIsSubmitting(true);
    try {
      // Uniqueness is enforced by a unique index on `email`, so a duplicate comes
      // back as a 409. Checking first would need read access to the collection,
      // which guests must not have.
      await databases.createDocument(
        window.AppConfig.DATABASE_ID,
        window.AppConfig.COLLECTION_ID,
        ID.unique(),
        { email: trimmedEmail }
      );

      setError("");
      setSuccess("Email added successfully in waitlist!");
      setEmail("");
    } catch (err) {
      console.error(err);
      setSuccess("");
      const isDuplicate =
        err?.code === 409 || err?.type === "document_already_exists";
      if (isDuplicate) {
        setError("You're already on the waitlist!");
      } else {
        setError("Something went wrong. Try again.");
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleOnchange = (e) => {
    setSuccess("");
    setError("");
    setEmail(e.target.value.toLowerCase());
  };

  return (
    <div className="w-[100%] rounded-[40px] h-[40%] bg-[#D8F1FF] flex flex-col items-center mq450:max-h-[526px] mq450:px-[10px] justify-center py-[12vh] px-[79px] mq750:px-[10px] box-border relative gap-[26px] max-w-full max-h-full overflow-auto text-center text-lgi text-black font-inter">
      <div className="relative text-6xl mq450:text-11xl leading-[110%] z-[0] text-43xl-4 font-libre-baskerville">
        <i>
          <span className="tracking-[-0.6px] text-transparent !bg-clip-text [background:linear-gradient(97.56deg,_#472723,_#110302)] [-webkit-background-clip:text] [-webkit-text-fill-color:transparent]">
            Closetrove
          </span>
          <span className="text-41xl tracking-[-1px] text-gray-100">{` `}</span>
          <span className="text-43xl-4 tracking-[-0.6px] text-transparent !bg-clip-text [background:linear-gradient(97.56deg,_#472723,_#110302)] [-webkit-background-clip:text] [-webkit-text-fill-color:transparent]">
            App
          </span>
        </i>
        <span className="text-41xl tracking-[-1px]"> is underway</span>
      </div>
      <div className="relative leading-[20px] font-light text-gray-200 z-[1]">
        Drop your email here and we will keep you posted.
      </div>
      <form
        onSubmit={handleSubmit}
        className="self-stretch rounded-81xl [background:linear-gradient(97.56deg,_rgba(71,_39,_35,_0.12),_rgba(17,_3,_2,_0.12))] overflow-hidden mq450:h-[40px] flex flex-row items-center justify-between p-1.5 z-[2] border-[3px] border-solid border-slate-400"
      >
        <input
          className="[border:none] px-2 w-[68%] text-11xl mq750:text-4xl mq450:text-sm mq750:w-[58%] [outline:none] py-0 bg-[transparent] mq450:h-[40px] relative h-[55px] overflow-hidden shrink-0"
          type="email"
          value={email}
          onChange={handleOnchange}
          autoComplete="off"
          required
        />
        <button
          type="submit"
          disabled={isSubmitting}
          className="cursor-pointer w-[30%] mq1050:w-[40%] mq750:w-[34%] [border:none] py-1 mq450:py-0 mq750:h-12 mq450:h-10 px-[20px] mq450:px-2 bg-[transparent] rounded-81xl [background:linear-gradient(97.56deg,_#472723,_#110302)] h-14 flex flex-row items-center justify-between box-border disabled:cursor-not-allowed disabled:opacity-60"
        >
          <div className="relative text-[16px] mq750:text-[12px] mq450:py-0 mq1050:text-[14px] leading-[16px] font-libre-baskerville text-white text-center">
            {isSubmitting ? "Joining..." : "Join The Waitlist"}
          </div>
          <div className="w-[14.2px] relative text-[15.9px] mq750:hidden leading-[16px] font-font-awesome-5-free text-white text-center flex items-center justify-center h-4 shrink-0">
            <FiArrowUpRight />
          </div>
        </button>
      </form>
      <div className="flex flex-col justify-start items-start">
        {error && <div className="text-red-600">{error}</div>}
        {success && <div className="text-green-600">{success}</div>}
      </div>
      <div className="w-[79px] !m-[0] absolute top-[19px] right-[30px] flex flex-row items-center justify-between z-[3] text-gray-100">
        <div className="flex items-center justify-center relative leading-[20px] font-light">
          <div className="pr-2">Close</div>
          <RxCross2
            onClick={() => setShowModal(!showModal)}
            className="cursor-pointer"
          />
        </div>
      </div>
    </div>
  );
};

export default PopUp;
